"use client";

import Logo from "@/assets/logo.png";
import DownloadsDropdown from "@/components/DownloadsDropdown";
import { ThemeToggle } from "@/components/providers/ThemeProvider";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/auth";
import { useDownloads } from "@/contexts/DownloadsContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import { useClientModules } from "@/hooks/useClientModules";
import type { Module } from "@/interfaces";
import api from "@/lib/axios";
import { cn, errorHandler, markdownExcerpt } from "@/lib/utils";
import type { UserNotifications } from "@/services/api/user";
import ReactMarkdown from "react-markdown";
import {
  type Announcement,
  type AnnouncementType,
  formatAnnouncementDateTime,
} from "@/services/api/announcements";
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CreditCard,
  HelpCircle,
  Info,
  Key,
  KeyRound,
  LogOut,
  Megaphone,
  Menu,
  Sparkles,
  User,
  Wrench,
  X,
} from "lucide-react";
import type React from "react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { AuthContext } from "../../../contexts/auth/AuthContext";
import { userService } from "../../../services/api/user";
import DownloadIndicator from "./DownloadIndicator";
import MenuItem from "./MenuItem";
import {
  buildModuleTree,
  findActiveRoute,
  normalizeRoute,
  resolveMenuEntry,
} from "./moduleTree";

function announcementVisual(type: AnnouncementType | undefined) {
  const t = type ?? "info";
  switch (t) {
    case "feature":
      return {
        label: "Funcionalidade",
        className: "text-emerald-600 dark:text-emerald-400",
        Icon: Sparkles,
      };
    case "maintenance":
      return {
        label: "Manutenção",
        className: "text-amber-600 dark:text-amber-400",
        Icon: CalendarClock,
      };
    case "warning":
      return {
        label: "Atenção",
        className: "text-red-600 dark:text-red-400",
        Icon: AlertTriangle,
      };
    default:
      return {
        label: "Informação",
        className: "text-blue-600 dark:text-blue-400",
        Icon: Info,
      };
  }
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarOpen, setIsSidebarOpen, isMobile } = useSidebar();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { logout } = useAuth();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isAnnouncementsPanelOpen, setIsAnnouncementsPanelOpen] =
    useState(false);
  const [isDownloadsOpen, setIsDownloadsOpen] = useState(false);
  const [notifications, setNotifications] = useState<UserNotifications[]>([]);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const announcementsPanelRef = useRef<HTMLDivElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<{
    message: string;
    date: string;
  } | null>(null);
  const [modalAnnouncement, setModalAnnouncement] =
    useState<Announcement | null>(null);
  const hasAutoOpenedAnnouncementsRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userInfoRef = useRef<HTMLDivElement>(null);
  const authContext = useContext(AuthContext);
  const userId = authContext?.userId ?? 0;
  const clientId = localStorage.getItem("verden-client-id");
  const isMasterUser = userId != 0 && String(userId) === clientId;

  const {
    announcements,
    recentAnnouncements,
    loading: announcementsLoading,
    refresh: refreshAnnouncements,
    refreshRecent: refreshRecentAnnouncements,
    markAsRead: markAnnouncementAsRead,
    markAllAsRead,
    unreadCount,
  } = useAnnouncements(userId);

  const { markDownloadsAsViewed } = useDownloads();

  const { data: flatModules, isLoading: modulesLoading } = useClientModules();
  const modules = useMemo(
    () => buildModuleTree(flatModules ?? []),
    [flatModules],
  );
  const activeRoute = useMemo(
    () => findActiveRoute(flatModules ?? [], location.pathname),
    [flatModules, location.pathname],
  );

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get(`/user/${userId}/notification?unread=1`);
      setNotifications(response.data.data || []);
    } catch (error) {
      errorHandler(error, "Erro ao buscar notificações");
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchNotifications();
  }, [userId, fetchNotifications]);

  useEffect(() => {
    hasAutoOpenedAnnouncementsRef.current = false;
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setModalAnnouncement(null);
      setIsAnnouncementsPanelOpen(false);
      return;
    }
    void refreshAnnouncements();
    void refreshRecentAnnouncements({ silent: true });
  }, [userId, refreshAnnouncements, refreshRecentAnnouncements]);

  useEffect(() => {
    if (!userId || announcementsLoading || announcements.length === 0) {
      return;
    }
    if (hasAutoOpenedAnnouncementsRef.current) {
      return;
    }
    hasAutoOpenedAnnouncementsRef.current = true;
    setModalAnnouncement(announcements[0]);
  }, [userId, announcementsLoading, announcements]);

  useEffect(() => {
    if (modalAnnouncement === null) {
      return;
    }
    const stillThere =
      announcements.some((a) => a.id === modalAnnouncement.id) ||
      recentAnnouncements.some((a) => a.id === modalAnnouncement.id);
    if (!stillThere) {
      setModalAnnouncement(null);
    }
  }, [announcements, recentAnnouncements, modalAnnouncement]);

  useEffect(() => {
    if (modulesLoading) return;
    if (userId && modules.length === 0 && location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [userId, modules, modulesLoading, location.pathname, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.getElementById("sidebar");
      const toggleButton = document.getElementById("sidebar-toggle");
      if (
        isMobile &&
        isSidebarOpen &&
        sidebar &&
        !sidebar.contains(event.target as Node) &&
        toggleButton &&
        !toggleButton.contains(event.target as Node)
      ) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, isSidebarOpen, setIsSidebarOpen]);

  useEffect(() => {
    const handleClickOutsideDropdown = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        userInfoRef.current &&
        !userInfoRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideDropdown);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
  }, []);

  useEffect(() => {
    const handleClickOutsideNotifications = (event: MouseEvent) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node) &&
        !(event.target as Element).classList.contains("notification-button")
      ) {
        setIsNotificationsOpen(false);
      }
    };

    if (isNotificationsOpen) {
      document.addEventListener("mousedown", handleClickOutsideNotifications);
    }

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutsideNotifications,
      );
    };
  }, [isNotificationsOpen]);

  useEffect(() => {
    const handleClickOutsideAnnouncements = (event: MouseEvent) => {
      if (
        announcementsPanelRef.current &&
        !announcementsPanelRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest(
          '[data-panel="announcements-menu-trigger"]',
        )
      ) {
        setIsAnnouncementsPanelOpen(false);
      }
    };

    if (isAnnouncementsPanelOpen) {
      document.addEventListener("mousedown", handleClickOutsideAnnouncements);
    }

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutsideAnnouncements,
      );
    };
  }, [isAnnouncementsPanelOpen]);

  const handleNavigation = (route: string) => {
    navigate(route);
    if (isMobile) setIsSidebarOpen(false);
  };

  const handleLogout = async () => {
    await logout();
  };

  const renderModule = (module: Module) => {
    const { route, hasSubMenu, isActive, children } = resolveMenuEntry(
      module,
      activeRoute,
    );

    return (
      <MenuItem
        key={module.id}
        icon={module.icon}
        label={module.name}
        hasSubMenu={hasSubMenu}
        isActive={isActive}
        defaultOpen={hasSubMenu && isActive}
        onClick={() => route && handleNavigation(route)}
        route={route}
      >
        {hasSubMenu &&
          children.map((child) => (
            <Link
              key={child.id}
              to={child.route}
              onClick={() => isMobile && setIsSidebarOpen(false)}
              className={cn(
                "block text-sm py-2",
                normalizeRoute(child.route) === activeRoute
                  ? "text-primary dark:text-white font-medium"
                  : "text-gray-400 hover:text-foreground",
              )}
            >
              {child.name}
            </Link>
          ))}
      </MenuItem>
    );
  };

  const handleRequestPermission = async () => {
    if (Notification.permission === "denied") {
      toast.error(
        "As notificações foram bloqueadas. Para habilitar, acesse as configurações do seu navegador.",
        {
          duration: 5000,
        },
      );
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      toast.success("Notificações habilitadas!");
    } else {
      toast.error("Notificações desabilitadas.");
    }
  };

  const handleOpenAnnouncementsPanel = () => {
    const nextOpen = !isAnnouncementsPanelOpen;
    setIsAnnouncementsPanelOpen(nextOpen);
    setIsDownloadsOpen(false);
    setIsNotificationsOpen(false);
    if (nextOpen) {
      setIsDropdownOpen(false);
    }
  };

  const modalMeta = modalAnnouncement
    ? announcementVisual(modalAnnouncement.type)
    : null;

  const handleCloseModal = (announcement: Announcement) => {
    const wasRead = Boolean(announcement.is_read);
    setModalAnnouncement(null);
    if (!wasRead) {
      markAnnouncementAsRead(announcement.id);
    }
  };

  const handleDownloadsClick = () => {
    setIsDownloadsOpen(!isDownloadsOpen);
    setIsNotificationsOpen(false);
    setIsDropdownOpen(false);

    if (!isDownloadsOpen) {
      markDownloadsAsViewed();
    }
  };

  return (
    <>
      {/* Botão de alternância de sidebar para dispositivos móveis */}
      <button
        id="sidebar-toggle"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-30 p-2 rounded-lg bg-gray-900 dark:bg-gray-800 text-white hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors"
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity"
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        id="sidebar"
        className={`fixed z-50 left-0 top-0 h-full w-62
                 bg-white dark:bg-gray-900
                 shadow-lg dark:shadow-gray-950/50
               transition-transform duration-300 ease-in-out flex flex-col
                ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
                lg:top-6 lg:left-6 lg:bottom-6 lg:h-auto lg:rounded-2xl`}
      >
        {/* Logo */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-800">
          <img src={Logo || "/placeholder.svg"} alt="Logo" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <div className="space-y-1">{modules.map(renderModule)}</div>
        </nav>

        {/* Footer */}
        <div className="shrink-0 mt-auto border-t border-gray-200 dark:border-gray-800">
          {/* User Info */}
          <div className="p-2 relative">
            <div className="flex items-center justify-between">
              {/* User Info */}
              <div
                ref={userInfoRef}
                className="flex items-center cursor-pointer"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className="flex flex-row gap-2 ml-1 text-xs text-gray-900 dark:text-white font-medium items-center dark:hover:text-white py-2 transition-colors">
                  <ChevronUp size={24} className="p-0" />
                  {authContext?.userName}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                {/* Theme Toggle */}
                <ThemeToggle />

                <DownloadIndicator onClick={handleDownloadsClick} />
                <DownloadsDropdown
                  isOpen={isDownloadsOpen}
                  onClose={() => setIsDownloadsOpen(false)}
                  onViewAll={() => {
                    setIsDownloadsOpen(false);
                  }}
                />

                <div className="relative">
                  <button
                    className="notification-button text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors relative"
                    onClick={() => {
                      setIsNotificationsOpen(!isNotificationsOpen);
                      setIsDownloadsOpen(false);
                      setIsDropdownOpen(false);
                    }}
                  >
                    <Bell size={22} />
                    {/* Notification Badge */}
                    {notifications.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xxs flex items-center justify-center">
                        {notifications.length >= 50
                          ? "+" + notifications.length
                          : notifications.length}
                      </span>
                    )}
                  </button>

                  {isNotificationsOpen && (
                    <div
                      ref={notificationsRef}
                      className="absolute left-4 bottom-8 mb-2 w-88 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-90"
                    >
                      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                            Notificações
                          </h3>
                          {Notification.permission !== "granted" && (
                            <button
                              onClick={handleRequestPermission}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              Habilitar notificações
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length > 0 ? (
                          notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className="p-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                              onClick={async () => {
                                setSelectedNotification({
                                  message: notification.data.message,
                                  date: new Date(
                                    notification.created_at,
                                  ).toLocaleString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                  }),
                                });
                                await userService.setNotificationAsRead(
                                  notification.id,
                                );
                                await fetchNotifications();
                              }}
                            >
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                {new Date(
                                  notification.created_at,
                                ).toLocaleString("pt-BR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                })}
                              </div>
                              <div className="text-sm text-gray-900 dark:text-white line-clamp-2">
                                {markdownExcerpt(notification.data.message)}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
                            Nenhuma notificação não lida
                          </div>
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <div className="p-2 border-t border-gray-200 dark:border-gray-700 text-center">
                          <button
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            onClick={async () => {
                              await userService.setNotificationAsRead();
                              await fetchNotifications();
                            }}
                          >
                            Marcar todas como lidas
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Notification Dialog */}
              <Dialog
                open={!!selectedNotification}
                onOpenChange={(open) => {
                  if (!open) setSelectedNotification(null);
                }}
              >
                <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{selectedNotification?.date}</DialogTitle>
                  </DialogHeader>
                  <div className="text-left text-gray-900 dark:text-white space-y-3 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:text-base [&_h2]:font-semibold">
                    <ReactMarkdown>
                      {selectedNotification?.message ?? ""}
                    </ReactMarkdown>
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={() => setSelectedNotification(null)}
                      className="btn-primary dark:text-white"
                    >
                      OK
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Announcement Dialog */}
              <Dialog
                open={modalAnnouncement !== null}
                onOpenChange={(open) => {
                  if (!open && modalAnnouncement) {
                    handleCloseModal(modalAnnouncement);
                  }
                }}
              >
                <DialogContent
                  className="sm:max-w-4xl max-h-[85vh] overflow-y-auto [&>button]:hidden"
                  onPointerDownOutside={(e) => e.preventDefault()}
                >
                  {modalAnnouncement ? (
                    <>
                      <DialogHeader>
                        <div className="flex items-start gap-2">
                          {modalMeta && (
                            <modalMeta.Icon
                              className={`shrink-0 size-6 mt-0.5 ${modalMeta.className}`}
                              aria-hidden
                            />
                          )}
                          <div className="flex-1 text-left space-y-1">
                            <DialogTitle className="text-left leading-snug">
                              {modalAnnouncement.title}
                            </DialogTitle>
                            <p
                              className={`text-xs font-medium ${modalMeta?.className}`}
                            >
                              {modalMeta?.label}
                            </p>
                          </div>
                        </div>
                      </DialogHeader>
                      <div className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
                        {modalAnnouncement.description}
                      </div>
                      {modalAnnouncement.image_url ? (
                        <img
                          src={modalAnnouncement.image_url}
                          alt=""
                          className="w-full rounded-md border border-gray-200 dark:border-gray-600"
                        />
                      ) : null}
                      <DialogFooter>
                        <Button
                          type="button"
                          className="btn-primary dark:text-white"
                          onClick={() => {
                            const id = modalAnnouncement.id;
                            const wasRead = Boolean(modalAnnouncement.is_read);
                            setModalAnnouncement(null);
                            // Se já estava lido, não re-disparar a marcação.
                            if (!wasRead) {
                              markAnnouncementAsRead(id);
                              void refreshRecentAnnouncements({ silent: true });
                            }
                          }}
                        >
                          OK
                        </Button>
                      </DialogFooter>
                    </>
                  ) : null}
                </DialogContent>
              </Dialog>
            </div>

            {isDropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute left-4 bottom-14 mb-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-50"
              >
                <ul className="py-2">
                  <li className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Link
                      to="/my-invoices"
                      className="flex items-center text-sm text-gray-900 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white py-2 transition-colors"
                      onClick={() => isMobile && setIsSidebarOpen(false)}
                    >
                      <CreditCard size={16} className="mr-3" />
                      Minhas faturas
                    </Link>
                  </li>
                  <li className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Link
                      to="/my-service-orders"
                      className="flex items-center text-sm text-gray-900 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white py-2 transition-colors"
                      onClick={() => isMobile && setIsSidebarOpen(false)}
                    >
                      <Wrench size={16} className="mr-3" />
                      Ordens de Serviço
                    </Link>
                  </li>
                  <li className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Link
                      to="/my-data"
                      className="flex items-center text-sm text-gray-900 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white py-2 transition-colors"
                      onClick={() => isMobile && setIsSidebarOpen(false)}
                    >
                      <User size={16} className="mr-3" />
                      Meus dados
                    </Link>
                  </li>
                  {isMasterUser && (
                    <li className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <Link
                        to="/api-integration"
                        className="flex items-center text-sm text-gray-900 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white py-2 transition-colors"
                        onClick={() => isMobile && setIsSidebarOpen(false)}
                      >
                        <KeyRound size={16} className="mr-3" />
                        Integração via API
                      </Link>
                    </li>
                  )}
                  <li className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Link
                      to="/auth/forgot-password"
                      className="flex items-center text-sm text-gray-900 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white py-2 transition-colors"
                      onClick={() => isMobile && setIsSidebarOpen(false)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Key size={16} className="mr-3" />
                      Alterar senha
                    </Link>
                  </li>
                  <li className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <button
                      type="button"
                      data-panel="announcements-menu-trigger"
                      className="flex items-center text-sm text-gray-900 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white py-2 transition-colors w-full"
                      onClick={handleOpenAnnouncementsPanel}
                    >
                      <Megaphone size={16} className="mr-3" />
                      Anúncios
                      {unreadCount > 0 ? (
                        <span className="ml-auto min-w-4 h-4 px-1 bg-red-500 rounded-full text-white text-xxs flex items-center justify-center">
                          {unreadCount >= 50 ? `+${unreadCount}` : unreadCount}
                        </span>
                      ) : null}
                    </button>
                  </li>
                </ul>
              </div>
            )}
            {isAnnouncementsPanelOpen && (
              <div
                ref={announcementsPanelRef}
                className="absolute left-4 bottom-14 mb-2 w-88 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-60"
              >
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Anúncios
                  </h3>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {recentAnnouncements.length > 0 ? (
                    recentAnnouncements.map((ann) => {
                      const meta = announcementVisual(ann.type);
                      const Icon = meta.Icon;
                      return (
                        <div
                          key={ann.id}
                          className="p-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          onClick={() => {
                            setModalAnnouncement(ann);
                            setIsAnnouncementsPanelOpen(false);
                          }}
                        >
                          <div className="flex items-center gap-2 text-xs mb-1">
                            <Icon
                              className={`shrink-0 size-3.5 ${meta.className}`}
                              aria-hidden
                            />
                            <span className={`font-medium ${meta.className}`}>
                              {meta.label}
                            </span>
                            {ann.is_read ? (
                              <span className="text-gray-500 dark:text-gray-400">
                                Lido
                              </span>
                            ) : (
                              <span className="text-blue-600 dark:text-blue-400">
                                Novo
                              </span>
                            )}
                            <span className="text-gray-500 dark:text-gray-400 ml-auto">
                              {formatAnnouncementDateTime(ann.created_at)}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {ann.title}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 mt-0.5">
                            {ann.description}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
                      {announcementsLoading
                        ? "Carregando…"
                        : "Nenhum anúncio disponível"}
                    </div>
                  )}
                </div>
                {unreadCount > 0 && (
                  <div className="p-2 border-t border-gray-200 dark:border-gray-700 text-center">
                    <button
                      type="button"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      onClick={() => {
                        markAllAsRead();
                        setModalAnnouncement(null);
                      }}
                    >
                      Marcar todos como lidos
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Support Links */}
          <div className="px-4 pb-4 space-y-1">
            <button
              type="button"
              className="flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white py-2 transition-colors w-full text-left"
              onClick={() => {
                /* Open troichat widget */
                if (isMobile) setIsSidebarOpen(false);
                setTimeout(() => {
                  const fab = document.querySelector<HTMLButtonElement>(
                    "#troia-widget-root .tw-fab",
                  );
                  fab?.click();
                }, 100);
              }}
            >
              <HelpCircle size={16} className="mr-3" />
              Suporte
            </button>

            {/* Logout Button */}
            <button
              className="flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white py-2 transition-colors w-full text-left"
              onClick={() => {
                if (isMobile) {
                  setIsSidebarOpen(false);
                }
                handleLogout();
              }}
            >
              <LogOut size={16} className="mr-3" />
              Sair
            </button>
          </div>
        </div>
      </aside>

      {/* Botão de colapso para desktop */}
      <button
        id="sidebar-collapse"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className={`hidden lg:flex fixed z-50 w-8 h-8 items-center justify-center
                   rounded-full bg-gray-400 dark:bg-gray-800 text-white
                   hover:bg-gray-300 dark:hover:bg-gray-700 transition-all
                   shadow-md
                ${isSidebarOpen ? "left-62 lg:left-68" : "left-6"}`}
        style={{ transform: "translateX(-50%)", top: "7rem" }}
      >
        {isSidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </button>
    </>
  );
};

export default Sidebar;
